/**
 * Reset Password Page
 * For HR/HRO users to reset their password using a token
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import authService from '../services/authService';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password strength validation
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.resetPassword(token, newPassword);
      
      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background p-lg relative overflow-hidden
        before:content-[''] before:absolute before:top-[-10%] before:left-[-5%] before:w-[300px] before:h-[300px] 
        before:bg-accent-green before:rounded-full before:opacity-[0.06] before:animate-floatSlow before:z-0
        after:content-[''] after:absolute after:bottom-[-10%] after:right-[-5%] after:w-[400px] after:h-[400px] 
        after:bg-accent-terracotta after:rounded-full after:opacity-[0.06] after:animate-floatSlowReverse after:z-0">
        <div className="bg-white p-2xl rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] w-full max-w-[420px] 
          relative z-[1] border border-[rgba(46,46,46,0.08)] animate-slideUp text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h1 className="text-[24px] text-primary mb-sm font-semibold tracking-[-0.5px]">
            Password Reset Successful
          </h1>
          
          <p className="text-[15px] text-text-secondary mb-xl leading-[1.5]">
            Your password has been successfully reset. You can now login with your new password.
          </p>
          
          <button 
            className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md 
              text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
              hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-background p-lg relative overflow-hidden
      before:content-[''] before:absolute before:top-[-10%] before:left-[-5%] before:w-[300px] before:h-[300px] 
      before:bg-accent-green before:rounded-full before:opacity-[0.06] before:animate-floatSlow before:z-0
      after:content-[''] after:absolute after:bottom-[-10%] after:right-[-5%] after:w-[400px] after:h-[400px] 
      after:bg-accent-terracotta after:rounded-full before:opacity-[0.06] after:animate-floatSlowReverse after:z-0">
      <div className="bg-white p-2xl rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] w-full max-w-[420px] 
        relative z-[1] border border-[rgba(46,46,46,0.08)] animate-slideUp">
        <h1 className="text-[28px] text-primary mb-sm text-center font-semibold tracking-[-0.5px]">
          Reset Password
        </h1>
        <h2 className="text-[15px] text-text-secondary mb-xl text-center font-normal leading-[1.5]">
          Enter your new password
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-lg">
            <label htmlFor="newPassword" className="block mb-sm text-text-primary font-medium text-[14px]">
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={isLoading}
              autoFocus
              className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
                transition-all duration-200 box-border bg-surface text-text-primary
                focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
            />
            <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
              Must be at least 8 characters with uppercase, lowercase, and numbers
            </small>
          </div>

          <div className="mb-lg">
            <label htmlFor="confirmPassword" className="block mb-sm text-text-primary font-medium text-[14px]">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={isLoading}
              className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
                transition-all duration-200 box-border bg-surface text-text-primary
                focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {error && (
            <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg 
              text-[14px] border-l-[3px] border-status-error leading-[1.5]">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md 
              text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
              hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md hover:enabled:bg-accent-green-dark
              active:translate-y-0 active:enabled:translate-y-0
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            disabled={isLoading || !token}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-xl pt-lg border-t border-border-light text-center">
          <p className="text-text-secondary text-[14px] mb-md">
            Remember your password?
          </p>
          <Link 
            to="/login"
            className="inline-block w-full py-[14px] px-6 bg-surface text-accent-green border-[1.5px] border-accent-green 
              rounded-md text-[15px] font-semibold cursor-pointer transition-all duration-200 text-center no-underline
              hover:bg-accent-green hover:text-surface hover:-translate-y-[1px] hover:shadow-md
              active:translate-y-0"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;